'use client';

import { useState } from 'react';
import Link from 'next/link';

interface FAQItem {
  id?: number;
  question_fa?: string;
  answer_fa?: string;
  question_en?: string;
  answer_en?: string;
  category?: string;
}

interface HomeFaqSectionProps {
  lang: string;
  faqs?: FAQItem[];
}

export default function HomeFaqSection({ lang, faqs }: HomeFaqSectionProps) {
  const isFa = lang === 'fa';

  const defaultFaqs: FAQItem[] = [
    {
      id: 1,
      question_fa: 'چگونه می‌توانم چالش را شروع کنم؟',
      answer_fa: 'کافیست در سایت ثبت‌نام کرده و از بخش پنل کاربری، پلن و سایز حساب مورد نظر خود را خریداری کنید. بلافاصله پس از تکمیل پرداخت، اطلاعات حساب متاتریدر ۵ برای شما صادر و ارسال می‌گردد.',
      question_en: 'How can I start the challenge?',
      answer_en: 'Simply register on the platform, choose your desired challenge size, and complete the order. Your MT5 credentials will be instantly generated and delivered.',
      category: 'عمومی'
    },
    {
      id: 2,
      question_fa: 'آیا محدودیت زمانی برای عبور از مراحل چالش وجود دارد؟',
      answer_fa: 'خیر! در پلتفرم پس‌فاند هیچ محدودیت زمانی اجباری برای رسیدن به تارگت‌های سود مرحله اول (۸٪) و مرحله دوم (۵٪) وجود ندارد. شما می‌توانید با خیال راحت و طبق استراتژی و مدیریت ریسک خود ترید کنید.',
      question_en: 'Is there a time limit to pass the challenge?',
      answer_en: 'No! There are no mandatory time limits to hit profit targets in Phase 1 (8%) or Phase 2 (5%). Trade comfortably at your own pace.',
      category: 'قوانین معاملاتی'
    },
    {
      id: 3,
      question_fa: 'قوانین دراوداون روزانه و کل چگونه محاسبه می‌شوند؟',
      answer_fa: 'حداکثر دراوداون روزانه ۵٪ و حداکثر دراوداون کل ۱۰٪ بالانس اولیه حساب می‌باشد. دراوداون روزانه بر اساس بالانس شروع روز سرور ریست می‌شود و اکوئیتی حساب نباید از حد مجاز افت کند.',
      question_en: 'How are daily and max drawdowns calculated?',
      answer_en: 'Daily drawdown is 5% and maximum total drawdown is 10% of initial balance. Daily limit resets at server start-of-day equity/balance.',
      category: 'قوانین معاملاتی'
    },
    {
      id: 4,
      question_fa: 'شرایط و زمان برداشت سود (Profit Split) به چه شکل است؟',
      answer_fa: 'معامله‌گران پس از ورود به مرحله فاندد (Real)، می‌توانند تا ۸۰ الی ۹۰ درصد از سود کسب شده خود را هر ۱۴ روز یکبار درخواست داده و در کمتر از ۲۴ ساعت کاری از طریق ارزهای دیجیتال (USDT) دریافت کنند.',
      question_en: 'What are the profit split and withdrawal terms?',
      answer_en: 'Once funded, you can withdraw 80% to 90% of profits bi-weekly via USDT crypto within 24 business hours.',
      category: 'برداشت سود'
    },
    {
      id: 5,
      question_fa: 'آیا معامله در زمان اخبار مهم اقتصادی و آخر هفته‌ها مجاز است؟',
      answer_fa: 'بله، معامله در زمان انتشار اخبار و همچنین باز نگه داشتن پوزیشن‌ها در تعطیلات آخر هفته کاملاً مجاز است، مشروط بر اینکه اصول مدیریت سرمایه و حد ضرر را رعایت کنید.',
      question_en: 'Is news and weekend holding permitted?',
      answer_en: 'Yes, news trading and weekend holding are permitted as long as proper risk parameters are respected.',
      category: 'قوانین معاملاتی'
    },
    {
      id: 6,
      question_fa: 'روش‌های پرداخت و شارژ حساب در پس‌فاند چیست؟',
      answer_fa: 'تمامی پرداخت‌ها و واریزها به صورت آنی و امن از طریق ارزهای دیجیتال تتر (USDT-TRC20) تحت درگاه پرداختی بین‌المللی پردازش و مستقیماً تایید می‌شوند.',
      question_en: 'What payment methods are supported?',
      answer_en: 'All payments and deposits are handled automatically and securely via USDT (TRC20) through an automated international crypto gateway.',
      category: 'عمومی'
    }
  ];

  const items = (faqs && faqs.length > 0) ? faqs : defaultFaqs;

  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const categories = ['all', ...Array.from(new Set(items.map((i) => i.category || 'عمومی'))).filter(Boolean)];

  const filteredItems = items.filter((item) => {
    if (selectedCategory === 'all') return true;
    return item.category === selectedCategory;
  });

  const toggleAccordion = (idx: number) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  return (
    <section className="home-faq-section" style={{ width: '100%', margin: '60px 0 20px 0' }}>
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <div 
          style={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: '8px', 
            backgroundColor: 'rgba(14, 165, 233, 0.1)', 
            border: '1px solid rgba(14, 165, 233, 0.25)', 
            color: '#0ea5e9', 
            padding: '6px 18px', 
            borderRadius: '30px', 
            fontSize: '13px', 
            fontWeight: '700',
            marginBottom: '14px'
          }}
        >
          <span style={{ width: '8px', height: '8px', backgroundColor: '#0ea5e9', borderRadius: '50%' }}></span>
          {isFa ? 'پاسخ به ابهامات شما' : 'Frequently Asked Questions'}
        </div>
        
        <h2 style={{ fontSize: 'clamp(24px, 3.5vw, 36px)', fontWeight: '900', color: '#0f172a', margin: '0 0 12px 0', letterSpacing: '-0.5px' }}>
          {isFa ? 'سوالات ' : 'Frequently Asked '}
          <span className="gradient-text">{isFa ? 'متداول تریدرها' : 'Questions'}</span>
        </h2>
        
        <p style={{ color: '#64748b', fontSize: '15px', maxWidth: '640px', margin: '0 auto', lineHeight: '1.8' }}>
          {isFa 
            ? 'پاسخ شفاف به پرتکرارترین پرسش‌های معامله‌گران درباره مراحل چالش، قوانین دراوداون و نحوه برداشت سود.' 
            : 'Clear answers to common questions about challenges, rules, and profit withdrawals.'}
        </p>

        {/* Category Filter Pills */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '24px' }}>
          {categories.map((cat) => {
            const isSelected = selectedCategory === cat;
            const label = cat === 'all' 
              ? (isFa ? 'همه سوالات' : 'All') 
              : cat;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                style={{
                  padding: '7px 16px',
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: isSelected ? '700' : '500',
                  border: isSelected ? '1px solid #0ea5e9' : '1px solid #e2e8f0',
                  backgroundColor: isSelected ? '#0ea5e9' : '#ffffff',
                  color: isSelected ? '#ffffff' : '#475569',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: isSelected ? '0 4px 12px rgba(14, 165, 233, 0.25)' : 'none'
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Accordion List */}
      <div style={{ maxWidth: '880px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {filteredItems.map((item, idx) => {
          const isOpen = openIndex === idx;
          const qText = isFa ? (item.question_fa || item.question_en) : (item.question_en || item.question_fa);
          const aText = isFa ? (item.answer_fa || item.answer_en) : (item.answer_en || item.answer_fa);

          return (
            <div
              key={item.id || idx}
              style={{
                backgroundColor: '#ffffff',
                border: isOpen ? '1px solid #0ea5e9' : '1px solid #e2e8f0',
                borderRadius: '16px',
                overflow: 'hidden',
                transition: 'all 0.25s ease',
                boxShadow: isOpen ? '0 10px 25px -5px rgba(14, 165, 233, 0.12)' : '0 2px 6px rgba(0,0,0,0.02)',
              }}
            >
              <button
                type="button"
                onClick={() => toggleAccordion(idx)}
                style={{
                  width: '100%',
                  padding: '18px 24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '16px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: isFa ? 'right' : 'left',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                  <span
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '8px',
                      backgroundColor: isOpen ? '#e0f2fe' : '#f1f5f9',
                      color: isOpen ? '#0284c7' : '#64748b',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '13px',
                      fontWeight: '800',
                      flexShrink: 0,
                    }}
                  >
                    Q
                  </span>
                  <span style={{ fontSize: '15px', fontWeight: '700', color: isOpen ? '#0284c7' : '#0f172a', lineHeight: '1.5' }}>
                    {qText}
                  </span>
                </div>

                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: isOpen ? '#0ea5e9' : '#f8fafc',
                    color: isOpen ? '#ffffff' : '#64748b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'all 0.25s ease',
                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
              </button>

              {isOpen && (
                <div
                  style={{
                    padding: '0 24px 20px 24px',
                    color: '#475569',
                    fontSize: '14px',
                    lineHeight: '1.8',
                    borderTop: '1px solid #f1f5f9',
                    paddingTop: '16px',
                  }}
                >
                  {aText}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* View All FAQs Link */}
      <div style={{ textAlign: 'center', marginTop: '32px' }}>
        <Link
          href={`/${lang}/faq`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            color: '#0284c7',
            backgroundColor: '#e0f2fe',
            border: '1px solid #bae6fd',
            padding: '10px 22px',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '700',
            textDecoration: 'none',
            transition: 'all 0.2s',
          }}
        >
          {isFa ? 'مشاهده تمامی سوالات متداول و آموزش‌ها' : 'View All FAQs & Knowledgebase'}
          <span>{isFa ? '←' : '→'}</span>
        </Link>
      </div>
    </section>
  );
}
